
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { uploadFileToOss } from './ossUpload';

const MB = 1024 * 1024;

interface ImageSize {
  width: number;
  height: number;
}

interface UploadedPhoto extends ImageSize {
  id: string;
  name: string;
  file: File;
  url: string;
  uploadUrl?: string;
}

export default Vue.extend({
  name: 'PhotoUploader',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    maxCount: {
      type: Number,
      default: undefined,
    },
    minSizeMb: {
      type: Number,
      default: undefined,
    },
    maxSizeMb: {
      type: Number,
      default: undefined,
    },
    minLongSide: {
      type: Number,
      default: undefined,
    },
    minWidth: {
      type: Number,
      default: undefined,
    },
    minHeight: {
      type: Number,
      default: undefined,
    },
    ruleTip: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      validating: false,
      uploadErrors: [] as string[],
      photoFiles: [] as UploadedPhoto[],
    };
  },
  computed: {
    triggerTip(): string {
      const countText = this.maxCount ? `最多${this.maxCount}张` : '支持多张';
      return `${countText} JPG/JPEG 图片`;
    },
    ruleText(): string {
      if (this.ruleTip) {
        return this.ruleTip;
      }

      const rules = ['JPG/JPEG格式'];
      if (this.minSizeMb || this.maxSizeMb) {
        const minText = this.minSizeMb ? `${this.minSizeMb}` : '0';
        const maxText = this.maxSizeMb ? `${this.maxSizeMb}` : '不限';
        rules.unshift(`单张${minText}-${maxText}MB`);
      }
      if (this.minLongSide) {
        rules.push(`图片长边不低于${this.minLongSide}像素`);
      }
      if (this.minWidth) {
        rules.push(`宽不低于${this.minWidth}像素`);
      }
      if (this.minHeight) {
        rules.push(`高不低于${this.minHeight}像素`);
      }
      return rules.join('，');
    },
  },
  beforeDestroy() {
    this.photoFiles.forEach((item) => URL.revokeObjectURL(item.url));
  },
  methods: {
    hasFiles(): boolean {
      return Boolean(this.photoFiles.length);
    },
    isValidating(): boolean {
      return this.validating;
    },
    clear() {
      this.photoFiles.forEach((item) => URL.revokeObjectURL(item.url));
      this.photoFiles = [];
      this.uploadErrors = [];
      this.emitChange();
    },
    setInitialFiles(urls: string[]) {
      this.clear();
      this.photoFiles = urls
        .filter(Boolean)
        .map((url, index) => this.createRemotePhoto(url, index));
      this.emitChange();
    },
    createRemotePhoto(url: string, index: number): UploadedPhoto {
      return {
        id: `remote-${index}-${url}`,
        name: this.getFileNameFromUrl(url) || `已上传图片${index + 1}`,
        file: null as any,
        url,
        uploadUrl: url,
        width: 0,
        height: 0,
      };
    },
    getFileNameFromUrl(url: string): string {
      const path = url.split('?')[0].split('#')[0];
      try {
        return decodeURIComponent(path.split('/').pop() || '');
      } catch (err) {
        return path.split('/').pop() || '';
      }
    },
    emitChange() {
      this.$emit('change', this.photoFiles);
    },
    setValidating(validating: boolean) {
      this.validating = validating;
      this.$emit('validating-change', validating);
    },
    openFilePicker() {
      const input = this.$refs.photoInput as HTMLInputElement;
      if (input && !this.validating && !this.disabled) {
        input.click();
      }
    },
    handleFileChange(event: Event) {
      const input = event.target as HTMLInputElement;
      this.validateFiles(input.files);
      input.value = '';
    },
    handleDrop(event: DragEvent) {
      if (this.validating || this.disabled) {
        return;
      }
      this.validateFiles(event.dataTransfer ? event.dataTransfer.files : null);
    },
    async validateFiles(fileList: FileList | null) {
      if (!fileList || !fileList.length) {
        return;
      }

      this.setValidating(true);
      this.uploadErrors = [];
      const accepted: UploadedPhoto[] = [];
      const errors: string[] = [];
      const files = Array.prototype.slice.call(fileList) as File[];
      const restCount = this.maxCount
        ? Math.max(this.maxCount - this.photoFiles.length, 0)
        : files.length;

      if (this.maxCount && files.length > restCount) {
        errors.push(`最多上传${this.maxCount}张图片`);
      }

      for (const file of files.slice(0, restCount)) {
        try {
          accepted.push(await this.validatePhoto(file));
        } catch (err) {
          errors.push(
            `${file.name}：${err instanceof Error ? err.message : err}`,
          );
        }
      }

      this.photoFiles = this.photoFiles.concat(accepted);
      this.uploadErrors = errors;
      this.setValidating(false);
      this.emitChange();

      if (accepted.length) {
        message.success(`已添加${accepted.length}张摄影作品`);
      }
      if (errors.length) {
        message.error('部分图片不符合上传要求');
      }
    },
    async validatePhoto(file: File): Promise<UploadedPhoto> {
      if (
        !/\.jpe?g$/i.test(file.name) ||
        (file.type && file.type !== 'image/jpeg')
      ) {
        throw new Error('仅支持JPG或JPEG格式');
      }
      if (this.minSizeMb && file.size < this.minSizeMb * MB) {
        throw new Error(`单张图片大小不能低于${this.minSizeMb}MB`);
      }
      if (this.maxSizeMb && file.size > this.maxSizeMb * MB) {
        throw new Error(`单张图片大小不能超过${this.maxSizeMb}MB`);
      }

      const imageSize = await this.getImageSize(file);
      if (
        this.minLongSide &&
        Math.max(imageSize.width, imageSize.height) < this.minLongSide
      ) {
        throw new Error(`图片长边不低于${this.minLongSide}像素`);
      }
      if (this.minWidth && imageSize.width < this.minWidth) {
        throw new Error(`图片宽不低于${this.minWidth}像素`);
      }
      if (this.minHeight && imageSize.height < this.minHeight) {
        throw new Error(`图片高不低于${this.minHeight}像素`);
      }

      const url = URL.createObjectURL(file);
      return {
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        file,
        url,
        width: imageSize.width,
        height: imageSize.height,
      };
    },
    getImageSize(file: File): Promise<ImageSize> {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
          URL.revokeObjectURL(url);
          resolve({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        };
        image.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('图片读取失败'));
        };
        image.src = url;
      });
    },
    removePhoto(index: number) {
      if (this.disabled) {
        return;
      }

      const removed = this.photoFiles.splice(index, 1)[0];
      if (removed) {
        URL.revokeObjectURL(removed.url);
      }
      this.emitChange();
    },
    async uploadPhotoToOss(photo: UploadedPhoto): Promise<string> {
      if (photo.uploadUrl) {
        return photo.uploadUrl;
      }

      const uploadUrl = await uploadFileToOss(photo);
      this.$set(photo, 'uploadUrl', uploadUrl);
      return uploadUrl;
    },
    uploadToOss(): Promise<string[]> {
      return Promise.all(
        this.photoFiles.map((item) => this.uploadPhotoToOss(item)),
      );
    },
  },
});
